// SPDX-License-Identifier: AGPL-3.0-or-later
import Channels from '@app/features/channel/state/Channels';
import DeveloperOptions from '@app/features/devtools/state/DeveloperOptions';
import GuildNSFWAgree, {NSFWGateReason} from '@app/features/guild/state/GuildNSFWAgree';
import Guilds from '@app/features/guild/state/Guilds';
import {getEffectiveChannelNsfw} from '@app/features/messaging/utils/ContentWarningUtils';
import Relationships from '@app/features/relationship/state/Relationships';
import UserSettings from '@app/features/user/state/UserSettings';
import {ChannelTypes} from '@app/vendor/constants/ChannelConstants';
import {RelationshipTypes, SensitiveMediaFilterLevel} from '@app/vendor/constants/UserConstants';
import {useCallback, useEffect, useState} from 'react';

interface NSFWMediaResult {
	shouldBlur: boolean;
	shouldBlock: boolean;
	gateReason: NSFWGateReason;
	canReveal: boolean;
	reveal: () => void;
}
function getSensitiveFilterLevel(channelId: string | undefined): number {
	if (!channelId) {
		return SensitiveMediaFilterLevel.SHOW;
	}
	const channel = Channels.getChannel(channelId);
	if (!channel) {
		return SensitiveMediaFilterLevel.SHOW;
	}
	if (channel.type === ChannelTypes.DM || channel.type === ChannelTypes.GROUP_DM) {
		if (channel.type === ChannelTypes.DM && channel.recipientIds.length > 0) {
			const recipientId = channel.recipientIds[0];
			const relationship = Relationships.getRelationship(recipientId);
			if (relationship?.type === RelationshipTypes.FRIEND) {
				return UserSettings.sensitiveContentFriendDmFilter;
			}
		}
		return UserSettings.sensitiveContentNonFriendDmFilter;
	}
	if (channel.guildId) {
		const guild = Guilds.getGuild(channel.guildId);
		if (getEffectiveChannelNsfw(channel, guild)) {
			return SensitiveMediaFilterLevel.SHOW;
		}
		return UserSettings.sensitiveContentGuildFilter;
	}
	return SensitiveMediaFilterLevel.SHOW;
}
export function useNSFWMedia(nsfw: boolean | undefined, channelId: string | undefined): NSFWMediaResult {
	const mockNSFWMediaGateReason = DeveloperOptions.mockNSFWMediaGateReason;
	const forceMediaNSFW = DeveloperOptions.forceMediaNSFW;
	const [isRevealed, setIsRevealed] = useState(false);
	const hasGateableContext = channelId ? GuildNSFWAgree.isGatedContent({channelId}) : false;
	const effectiveNSFW = forceMediaNSFW || mockNSFWMediaGateReason !== 'none' || !!nsfw || hasGateableContext;
	const gateReasonFromState = GuildNSFWAgree.getGateReason({channelId: channelId ?? null});
	let gateReason: NSFWGateReason;
	if (mockNSFWMediaGateReason !== 'none') {
		gateReason =
			mockNSFWMediaGateReason === 'geo_restricted' ? NSFWGateReason.GEO_RESTRICTED : NSFWGateReason.AGE_RESTRICTED;
	} else if (effectiveNSFW && channelId) {
		gateReason = gateReasonFromState;
	} else {
		gateReason = NSFWGateReason.NONE;
	}
	const filterLevel = getSensitiveFilterLevel(channelId);
	const isFilterBlur = effectiveNSFW && filterLevel === SensitiveMediaFilterLevel.BLUR;
	const isFilterBlock = effectiveNSFW && filterLevel === SensitiveMediaFilterLevel.BLOCK;
	const isGateBlur =
		effectiveNSFW && gateReason !== NSFWGateReason.NONE && gateReason !== NSFWGateReason.CONSENT_REQUIRED;
	const canReveal = isFilterBlur && !isGateBlur;
	const shouldBlur = (isGateBlur || isFilterBlur) && !(canReveal && isRevealed);
	const shouldBlock: boolean = isFilterBlock;
	useEffect(() => {
		setIsRevealed(false);
	}, [channelId, gateReason, mockNSFWMediaGateReason, forceMediaNSFW, nsfw, filterLevel]);
	const reveal = useCallback(() => {
		if (!canReveal) return;
		setIsRevealed(true);
	}, [canReveal]);
	return {shouldBlur, gateReason, shouldBlock, canReveal, reveal};
}
